/**
 * useProgramVisitTracker
 *
 * Fires a single, fire-and-forget `recordProgramVisit` per share-link page-load.
 * Centralizes every guard so the route chokepoints (public-program Setup, the
 * App.vue unauth /guide watcher, ParticipantGuide) just call one function.
 *
 * Guards (in order):
 *   1. Feature flag PROGRAM_VISIT_ANALYTICS (kill switch, no redeploy).
 *   2. Self-view exclusion — the program's own creator/organizers don't count.
 *   3. Once-per-session-per-program (sessionStorage) so reloads don't inflate.
 *
 * It NEVER throws and NEVER blocks render — analytics must not affect a
 * visitor's experience. Reuses useVisitorId (unique-visitor UUID) +
 * useAcquisitionMetadata (referrer/utm/device) for the payload. Country is
 * derived server-side from cf-ipcountry; no IP is ever sent.
 */

import nexusClient from '@/apollo/nexusClient';
import { useVisitorId } from '@/composables/useVisitorId';
import { useAcquisitionMetadata } from '@/composables/useAcquisitionMetadata';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { PREVIEW_FENCE_EXEMPT } from '@/utils/previewGuard';
import {
  RECORD_PROGRAM_VISIT_MUTATION,
  type RecordProgramVisitInput,
} from '@/services/graphql/nexus/programVisit';

/** Normalize a possibly-wrapped id (string | { $oid } | { _id } | ObjectId-ish) to a hex string. */
function idStr(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (typeof value.$oid === 'string') return value.$oid;
    if (typeof value._id === 'string') return value._id;
    if (typeof value.userId === 'string') return value.userId;
    try {
      const s = value.toString();
      if (/^[0-9a-fA-F]{24}$/.test(s)) return s;
    } catch {
      /* no-op */
    }
  }
  return null;
}

function isSelfView(
  currentUserId: string | null | undefined,
  createdBy: any,
  organizers: any[] | undefined,
): boolean {
  const uid = idStr(currentUserId);
  if (!uid) return false;
  if (idStr(createdBy) === uid) return true;
  if (Array.isArray(organizers)) {
    for (const org of organizers) {
      // organizer entries are typically { userId, type, ... } or a bare id.
      if (idStr(org?.userId ?? org) === uid) return true;
    }
  }
  return false;
}

export function useProgramVisitTracker() {
  const { getOrCreateVisitorId } = useVisitorId();
  const { collect } = useAcquisitionMetadata();

  const trackProgramVisit = async (
    programId: string,
    opts?: {
      currentUserId?: string | null;
      createdBy?: any;
      organizers?: any[];
    },
  ): Promise<void> => {
    try {
      if (!FEATURE_FLAGS.PROGRAM_VISIT_ANALYTICS) return;
      if (!programId || typeof window === 'undefined') return;

      // 2. Don't count the creator/organizers viewing their own program.
      if (isSelfView(opts?.currentUserId, opts?.createdBy, opts?.organizers)) {
        return;
      }

      // 3. Once per session per program (a reload within the tab won't re-fire).
      const key = `pcv-visited-${programId}`;
      try {
        if (window.sessionStorage?.getItem(key)) return;
        window.sessionStorage?.setItem(key, '1');
      } catch {
        /* storage blocked — proceed without the dedupe guard */
      }

      const meta = collect();
      const input: RecordProgramVisitInput = {
        programId,
        visitorId: getOrCreateVisitorId(),
        referrer: meta.referrer,
        utmSource: meta.utmSource,
        utmMedium: meta.utmMedium,
        utmCampaign: meta.utmCampaign,
        language: meta.language,
        timezone: meta.timezone,
      };

      await nexusClient.mutate({
        mutation: RECORD_PROGRAM_VISIT_MUTATION,
        variables: { input },
        // A visitor on someone's /public-program page reads behind the write fence; their
        // own visit is still counted. (The preview link never calls this at all.)
        context: { [PREVIEW_FENCE_EXEMPT]: true },
      });
    } catch {
      // Fire-and-forget — a failed visit log must never surface to the visitor.
    }
  };

  return { trackProgramVisit };
}
